import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import {
  LayoutDashboard,
  Users,
  Utensils,
  FolderTree,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  Leaf,
  UserCog,
  Building2,
  BadgePercent
} from 'lucide-react';
import { Button } from './ui/button';
import { cn, API_BASE_URL } from '../lib/utils';
import ThemeToggle from './ThemeToggle';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: FolderTree, label: 'Food Categories', path: '/food-categories' },
  { icon: Utensils, label: 'Food Items', path: '/food-items' },
  { icon: MessageSquare, label: 'Groups & Chat', path: '/groups' },
  { icon: UserCog, label: 'Agents', path: '/agents' },
  { icon: Building2, label: 'Partners', path: '/partners' },
  { icon: BadgePercent, label: 'Resellers', path: '/resellers' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branding } = useBranding();

  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-full bg-card border-r border-border z-40 transition-all duration-300',
        collapsed ? 'w-20' : 'w-64'
      )}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-between gap-2 px-4 py-5 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            {branding?.logo_url ? (
              <img
                src={`${API_BASE_URL}${branding.logo_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
                alt={branding?.brand_name || 'Logo'}
                className="w-10 h-10 rounded object-contain shadow"
              />
            ) : (
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center shadow">
                <Leaf className="w-5 h-5 text-white" />
              </div>
            )}
            {!collapsed && (
              <span className="font-bold text-lg text-foreground tracking-tight truncate">{branding?.brand_name || 'WingsDiet'}</span>
            )}
          </div>
          {!collapsed && <ThemeToggle />}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'nav-item flex items-center gap-3 px-3 py-2.5 rounded-md transition-all',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <item.icon className={cn('w-5 h-5', isActive && 'text-primary')} strokeWidth={1.5} />
                {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-3">
          {!collapsed && user && (
            <div className="px-3 py-2 mb-2">
              <p className="text-sm font-medium text-foreground truncate">{user.name || user.email}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10',
              collapsed && 'justify-center px-0'
            )}
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            {!collapsed && <span className="font-medium text-sm">Logout</span>}
          </Button>
        </div>

        {/* Collapse button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center shadow-sm hover:bg-muted transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
          ) : (
            <ChevronLeft className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
      </div>
    </aside>
  );
};

export const MobileHeader = ({ onMenuClick }) => {
  const { branding } = useBranding();
  return (
    <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 px-4 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        {branding?.logo_url ? (
          <img
            src={`${API_BASE_URL}${branding.logo_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
            alt={branding?.brand_name || 'Logo'}
            className="w-9 h-9 rounded object-contain"
          />
        ) : (
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <Leaf className="w-4 h-4 text-white" />
          </div>
        )}
        <span className="font-bold text-lg text-foreground truncate">{branding?.brand_name || 'WingsDiet'}</span>
      </div>
      <div className="flex items-center gap-1">
        <ThemeToggle />
        <Button variant="ghost" size="icon" onClick={onMenuClick} data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </Button>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Mobile header */}
      <MobileHeader onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)} />

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={cn(
          'md:hidden fixed left-0 top-0 h-full z-50 transition-transform duration-300',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar collapsed={false} setCollapsed={() => {}} />
      </div>

      {/* Main content */}
      <main
        className={cn(
          'transition-all duration-300 pt-16 md:pt-0',
          collapsed ? 'md:ml-20' : 'md:ml-64'
        )}
      >
        <div className="p-6 md:p-8">{children}</div>
      </main>
    </div>
  );
};
